import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    ArrowLeft,
    Shield,
    Calendar,
    DollarSign,
    Percent,
    Users,
    Building,
    Clock,
    AlertCircle,
    CheckCircle,
    FileText,
    RefreshCw,
    XCircle,
    TrendingUp,
    BarChart3,
    UserCheck,
    Eye,
    ChevronRight,
    Loader2,
    Printer,
    Download,
    Edit,
    Copy,
    Trash2,
    PlusCircle
} from 'lucide-react';
import api from '../../services/api';

export default function OpenCoverDetail() {
    const { id } = useParams();
    const { user, token } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const [cover, setCover] = useState(null);
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showRenewModal, setShowRenewModal] = useState(false);
    const [renewDays, setRenewDays] = useState(365);
    const [cancelReason, setCancelReason] = useState('');

    useEffect(() => {
        if (!token && !localStorage.getItem('token')) {
            navigate('/login');
            return;
        }
        fetchCoverDetails();
    }, [id, token, navigate]);

    const fetchCoverDetails = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await api.get(`/quotes/open-covers/${id}`);
            setCover(response.data);

            try {
                const quotesResponse = await api.get(`/quotes?open_cover_id=${id}`);
                setQuotes(quotesResponse.data || []);
            } catch (err) {
                console.warn('Failed to fetch quotes:', err);
                setQuotes([]);
            }

            setLoading(false);
        } catch (err) {
            console.error('Failed to load cover details:', err);

            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            } else if (err.response?.status === 404) {
                setError(`Open cover with ID ${id} not found`);
                setLoading(false);
            } else {
                setError(err.response?.data?.error || 'Failed to load cover details');
                setLoading(false);
            }
        }
    };

    const handleRenew = async () => {
        setRefreshing(true);
        try {
            await api.post(`/quotes/open-covers/${id}/renew`, { days: renewDays });
            setShowRenewModal(false);
            await fetchCoverDetails();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to renew cover');
        } finally {
            setRefreshing(false);
        }
    };

    const handleCancel = async () => {
        setRefreshing(true);
        try {
            await api.put(`/quotes/open-covers/${id}/cancel`, { reason: cancelReason });
            setShowCancelModal(false);
            await fetchCoverDetails();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to cancel cover');
        } finally {
            setRefreshing(false);
        }
    };

    if (!token && !localStorage.getItem('token')) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
                <div className="text-center max-w-md p-8">
                    <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Authentication Required</h3>
                    <p className="text-gray-500 dark:text-slate-400">Please log in to view this open cover.</p>
                    <Link to="/login" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#3a663f] text-white rounded-lg font-medium hover:bg-[#2d5231] transition-colors">
                        Go to Login
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse space-y-6">
                        <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded-lg w-1/4" />
                        <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
                        <div className="h-24 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="h-64 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                            <div className="h-64 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !cover) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
                <div className="text-center max-w-md p-8">
                    <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Open Cover Not Found</h3>
                    <p className="text-gray-500 dark:text-slate-400">{error || 'The requested open cover could not be found.'}</p>
                    <Link to="/open-covers" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#3a663f] text-white rounded-lg font-medium hover:bg-[#2d5231] transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Open Covers
                    </Link>
                </div>
            </div>
        );
    }

    const remaining = Number(cover.sum_insured_limit) - Number(cover.used_amount || 0);
    const usedPercent = (Number(cover.used_amount || 0) / Number(cover.sum_insured_limit)) * 100;
    const now = new Date();
    const endDate = new Date(cover.end_date);
    const isExpired = endDate <= now;
    const isExpiringSoon = !isExpired && endDate < new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

    const isAdmin = user?.role === 'admin';
    const isUnderwriter = user?.role === 'underwriter';
    const isAgentOrBroker = user?.role === 'agent' || user?.role === 'broker';
    const isClient = user?.role === 'direct_client';

    const canCancel = (isAdmin || isUnderwriter) && cover.status === 'active';
    const canRenew = isExpired && (isAdmin || isUnderwriter) && cover.is_renewable;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Navigation */}
                <div className="flex items-center justify-between print:hidden">
                    <Link to="/open-covers" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-[#4a7f4f] transition-colors font-medium">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Open Covers
                    </Link>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => window.print()}
                            className="p-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-[#4a7f4f] rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            <Printer className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchCoverDetails}
                            disabled={refreshing}
                            className="p-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-[#4a7f4f] rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Header Banner */}
                <div className={`relative overflow-hidden rounded-2xl p-8 text-white ${cover.status === 'active' ? 'bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54]' :
                        cover.status === 'expired' ? 'bg-gradient-to-br from-gray-700 to-gray-800' :
                            'bg-gradient-to-br from-red-700 to-red-800'
                    } shadow-2xl`}>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/3" />

                    <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                                <Shield className="w-8 h-8" />
                            </div>
                            <div>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="text-sm text-green-200 opacity-80">Open Cover</span>
                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cover.status === 'active' ? 'bg-green-500/30 text-green-200' :
                                            cover.status === 'expired' ? 'bg-gray-500/30 text-gray-200' :
                                                'bg-red-500/30 text-red-200'
                                        }`}>
                                        {cover.status?.toUpperCase() || 'ACTIVE'}
                                    </span>
                                    {isExpiringSoon && cover.status === 'active' && (
                                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/30 text-amber-200 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {daysRemaining} days left
                                        </span>
                                    )}
                                </div>
                                <h1 className="text-3xl md:text-4xl font-extrabold mt-1">{cover.cover_number}</h1>
                                <p className="text-green-100/80 mt-1">{cover.client_name}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                            <div className="text-center">
                                <p className="text-xs text-white/60">Total Limit</p>
                                <p className="font-bold text-lg">{cover.currency} {Number(cover.sum_insured_limit).toLocaleString()}</p>
                            </div>
                            <div className="w-px h-12 bg-white/20" />
                            <div className="text-center">
                                <p className="text-xs text-white/60">Remaining</p>
                                <p className={`font-bold text-lg ${remaining <= 0 ? 'text-red-300' : 'text-green-300'}`}>
                                    {cover.currency} {remaining.toLocaleString()}
                                </p>
                            </div>
                            <div className="w-px h-12 bg-white/20" />
                            <div className="text-center">
                                <p className="text-xs text-white/60">Quotes</p>
                                <p className="font-bold text-lg">{cover.quote_count || 0}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Usage Bar */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-slate-700">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                            <span className="font-medium text-gray-700 dark:text-slate-300">Usage</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-500 dark:text-slate-400">
                                Used: <strong className="text-gray-700 dark:text-white">{cover.currency} {Number(cover.used_amount || 0).toLocaleString()}</strong>
                            </span>
                            <span className="text-gray-500 dark:text-slate-400">
                                Remaining: <strong className={`${remaining <= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {cover.currency} {remaining.toLocaleString()}
                                </strong>
                            </span>
                            <span className={`font-bold ${usedPercent > 80 ? 'text-red-600' : usedPercent > 50 ? 'text-amber-600' : 'text-green-600'}`}>
                                {usedPercent.toFixed(1)}%
                            </span>
                        </div>
                    </div>
                    <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-500 ${usedPercent > 80 ? 'bg-red-500' :
                                    usedPercent > 50 ? 'bg-amber-500' :
                                        'bg-[#3a663f]'
                                }`}
                            style={{ width: `${Math.min(usedPercent, 100)}%` }}
                        />
                    </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <DetailCard title="Cover Details" icon={FileText}>
                        <DetailItem label="Cover Number" value={cover.cover_number} />
                        <DetailItem label="Client" value={cover.client_name} />
                        <DetailItem label="Currency" value={cover.currency} />
                        <DetailItem label="Marine Rate" value={`${cover.marine_rate}%`} />
                        <DetailItem label="War Rate" value={`${cover.war_rate}%`} />
                        <DetailItem label="Renewable" value={cover.is_renewable ? '✅ Yes' : '❌ No'} />
                        {cover.renewal_count > 0 && (
                            <DetailItem label="Renewals" value={cover.renewal_count} />
                        )}
                        <DetailItem label="Status" value={cover.status} valueClassName={`capitalize font-semibold ${cover.status === 'active' ? 'text-green-600 dark:text-green-400' :
                                cover.status === 'expired' ? 'text-gray-600 dark:text-gray-400' :
                                    'text-red-600 dark:text-red-400'
                            }`} />
                        {cover.broker_name && (
                            <DetailItem label="Broker" value={cover.broker_name} />
                        )}
                        {cover.created_by_role && (
                            <DetailItem label="Created By" value={cover.created_by_role} valueClassName="capitalize" />
                        )}
                    </DetailCard>

                    <DetailCard title="Period Information" icon={Calendar}>
                        <DetailItem
                            label="Start Date"
                            value={cover.start_date ? new Date(cover.start_date).toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                        />
                        <DetailItem
                            label="End Date"
                            value={cover.end_date ? new Date(cover.end_date).toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                            valueClassName={isExpiringSoon && cover.status === 'active' ? 'text-amber-600 dark:text-amber-400' : ''}
                        />
                        <DetailItem
                            label="Period Start"
                            value={cover.period_start ? new Date(cover.period_start).toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                        />
                        <DetailItem
                            label="Period End"
                            value={cover.period_end ? new Date(cover.period_end).toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                        />
                        <DetailItem label="Quotes Issued" value={cover.quote_count || 0} />
                        <DetailItem label="Used Amount" value={`${cover.currency} ${Number(cover.used_amount || 0).toLocaleString()}`} />
                        <DetailItem label="Remaining Amount" value={`${cover.currency} ${remaining.toLocaleString()}`} valueClassName={remaining <= 0 ? 'text-red-600' : 'text-green-600'} />
                    </DetailCard>
                </div>

                {/* Terms & Conditions */}
                {cover.terms_conditions && (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-slate-700">
                        <div className="flex items-center gap-2 mb-4">
                            <FileText className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                            <h3 className="font-bold text-gray-800 dark:text-white">Terms & Conditions</h3>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl p-4">
                            <p className="text-gray-600 dark:text-slate-300 whitespace-pre-line text-sm">{cover.terms_conditions}</p>
                        </div>
                    </div>
                )}

                {/* Quotes Section */}
                {quotes.length > 0 && (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                        <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                <h3 className="font-bold text-gray-800 dark:text-white">Quotes Issued</h3>
                                <span className="text-sm text-gray-500 dark:text-slate-400">({quotes.length})</span>
                            </div>
                            <Link
                                to={`/quotes?open_cover_id=${cover.id}`}
                                className="text-sm text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] font-medium flex items-center gap-1"
                            >
                                View All
                                <ChevronRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-slate-700">
                            {quotes.slice(0, 5).map((quote) => (
                                <Link
                                    key={quote.id}
                                    to={`/quotes/${quote.id}`}
                                    className="block p-4 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-[#3a663f]/10 dark:bg-[#3a663f]/20 rounded-lg">
                                                <FileText className="w-4 h-4 text-[#3a663f] dark:text-green-400" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-800 dark:text-white group-hover:text-[#3a663f] dark:group-hover:text-green-400 transition-colors">
                                                    {quote.quote_number}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                                    {quote.created_at ? new Date(quote.created_at).toLocaleDateString() : 'N/A'} •
                                                    {quote.currency} {Number(quote.total_premium || 0).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${quote.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                                                    quote.status === 'submitted' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                                                        quote.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                                                            'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                                                }`}>
                                                {quote.status || 'Draft'}
                                            </span>
                                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#3a663f] dark:group-hover:text-green-400 transition-colors" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                        {quotes.length > 5 && (
                            <div className="p-4 text-center border-t border-gray-100 dark:border-slate-700">
                                <Link
                                    to={`/quotes?open_cover_id=${cover.id}`}
                                    className="text-sm text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] font-medium"
                                >
                                    View all {quotes.length} quotes →
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4 print:hidden">
                    <Link
                        to="/open-covers"
                        className="flex-1 min-w-[120px] py-3 px-4 border border-gray-300 dark:border-slate-600 rounded-xl text-center text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium"
                    >
                        Back to List
                    </Link>

                    {canCancel && (
                        <button
                            onClick={() => setShowCancelModal(true)}
                            disabled={refreshing}
                            className="py-3 px-6 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl disabled:opacity-60 flex items-center gap-2"
                        >
                            <XCircle className="w-5 h-5" />
                            Cancel Cover
                        </button>
                    )}

                    {canRenew && (
                        <button
                            onClick={() => setShowRenewModal(true)}
                            disabled={refreshing}
                            className="py-3 px-6 bg-[#3a663f] hover:bg-[#2d5231] text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl disabled:opacity-60 flex items-center gap-2"
                        >
                            <RefreshCw className="w-5 h-5" />
                            Renew Cover
                        </button>
                    )}
                </div>

                {/* Cancel Modal */}
                {showCancelModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
                            <div className="text-center space-y-4">
                                <div className="w-16 h-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                    <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">Cancel Open Cover</h3>
                                    <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
                                        Are you sure you want to cancel this open cover? This action cannot be undone.
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 text-left mb-1">
                                        Reason for cancellation
                                    </label>
                                    <textarea
                                        value={cancelReason}
                                        onChange={(e) => setCancelReason(e.target.value)}
                                        placeholder="Please provide a reason for cancellation..."
                                        rows={3}
                                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500/50 focus:border-red-500 outline-none transition-all resize-none"
                                    />
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => setShowCancelModal(false)}
                                        className="flex-1 py-3 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleCancel}
                                        disabled={refreshing}
                                        className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-60 transition-colors shadow-lg"
                                    >
                                        {refreshing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Confirm Cancellation'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Renew Modal */}
                {showRenewModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
                            <div className="text-center space-y-4">
                                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                    <RefreshCw className="w-8 h-8 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">Renew Open Cover</h3>
                                    <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
                                        Extend the validity of this open cover.
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 text-left mb-1">
                                        Renewal Period (days)
                                    </label>
                                    <select
                                        value={renewDays}
                                        onChange={(e) => setRenewDays(Number(e.target.value))}
                                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                    >
                                        <option value="30">30 days</option>
                                        <option value="60">60 days</option>
                                        <option value="90">90 days</option>
                                        <option value="180">180 days</option>
                                        <option value="365">365 days</option>
                                        <option value="730">2 years</option>
                                    </select>
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => setShowRenewModal(false)}
                                        className="flex-1 py-3 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleRenew}
                                        disabled={refreshing}
                                        className="flex-1 py-3 bg-[#3a663f] text-white rounded-lg font-semibold hover:bg-[#2d5231] disabled:opacity-60 transition-colors shadow-lg"
                                    >
                                        {refreshing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Confirm Renewal'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function DetailCard({ title, icon: Icon, children }) {
    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-4">
                <Icon className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                <h3 className="font-bold text-gray-800 dark:text-white">{title}</h3>
            </div>
            <dl className="space-y-3 text-sm">
                {children}
            </dl>
        </div>
    );
}

function DetailItem({ label, value, valueClassName = '' }) {
    return (
        <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
            <dt className="text-gray-500 dark:text-slate-400">{label}</dt>
            <dd className={`font-medium text-gray-800 dark:text-white ${valueClassName}`}>
                {value || '-'}
            </dd>
        </div>
    );
}